import {messagesService} from "@dv5/api"
import {
    changeEditWidget,
    changeSelection,
    DataLink,
    DvCanvas,
    getCanvasContents,
    getCanvasRefreshRequests,
    getCanvasRevealRequest,
    getCanvasSavedScrollPositions,
    getCanvasSelectedLayerIds,
    getLayoutLayers,
    getCanvasMinimapVisibility,
    getWSConnectionStatus,
    setLayoutLayers,
    stripNoLayers,
    TagFiltered,
    useAppDispatch,
    useAppSelector
} from '@dv5/domain'
import React, {CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {CanvasContent} from "./containers/CanvasContent"
import {useCanvasSettings} from "./containers/hooks/useCanvasSettings"
import {CanvasToolbar} from './containers/CanvasToolbar'
import {useCanvasViewport} from "./containers/hooks/useCanvasViewport"
import {CanvasMinimap} from "./components/CanvasMinimap"
import {CanvasMode} from "./model/CanvasMode"

interface GetCanvas {
    canvasId: string
    layers: Array<string>
    tags: Array<string>
}

interface Props {
    canvas: DvCanvas
    zoom: number
    filteredTags: Array<TagFiltered>
    layoutId: string
    onNavigate: (dataLink: DataLink) => void
    onNavigateToDocument: (documentId: string, layoutId: string) => void
    onNavigateToTask: (taskId: string, layoutId: string) => void
}

export const CanvasView: FC<Props> = ({
                                          canvas,
                                          zoom,
                                          filteredTags,
                                          layoutId,
                                          onNavigate,
                                          onNavigateToDocument,
                                          onNavigateToTask,
                                      }) => {
    const {readOnly} = useCanvasSettings(canvas.id)
    const [mode, setMode] = useState<CanvasMode>('select')
    const [loaded, setLoaded] = React.useState(false)
    const layoutLayers = useAppSelector(getLayoutLayers)
    const selectedLayerIdsMap = useAppSelector(getCanvasSelectedLayerIds)
    const wsConnectionStatus = useAppSelector(getWSConnectionStatus)
    const previousConnectionStatus = useRef(wsConnectionStatus)
    const restoredLayersRef = useRef(false)
    const [currentLayers, setCurrentLayers] = useState<Array<string>>([])
    const dispatch = useAppDispatch()

    const minimapVisible = useAppSelector(state => getCanvasMinimapVisibility(state)[layoutId] ?? false)
    const canvasRef = useRef<HTMLDivElement | null>(null)
    const savedScrollPositions = useAppSelector(getCanvasSavedScrollPositions)
    const savedScrollPosition = useMemo(
        () => savedScrollPositions[canvas.id],
        [canvas.id, savedScrollPositions],
    )
    const canvasContents = useAppSelector(getCanvasContents)
    const refreshRequest = useAppSelector(getCanvasRefreshRequests)[canvas.id]
    useEffect(() => {
        setLoaded(false)
    }, [refreshRequest])
    const revealRequest = useAppSelector(getCanvasRevealRequest)
    const revealTarget = useMemo(() => {
        if (!revealRequest || revealRequest.canvasId !== canvas.id) {
            return undefined
        }
        const widget = (canvasContents[canvas.id]?.widgets || [])
            .find(candidate => candidate.id === revealRequest.widgetId)
        return widget ? {widget, nonce: revealRequest.nonce} : undefined
    }, [revealRequest, canvasContents, canvas.id])
    const {scrollPosition, isPanning, resetScroll, navigateTo} = useCanvasViewport(canvas.id, zoom, canvasRef, savedScrollPosition, revealTarget)

    const styles: CSSProperties = {
        position: 'relative',
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        alignSelf: 'stretch',
        display: 'block',
        boxSizing: 'border-box',
    }

    useEffect(() => {
        if (!loaded) {
            const tagIds = filteredTags.filter(tag => tag.checked).map(tag => tag.tag.id)
            messagesService.send<GetCanvas>("canvas.get", {
                canvasId: canvas.id,
                layers: currentLayers,
                tags: tagIds,
            })
            setLoaded(true)
        }
    }, [loaded, currentLayers, filteredTags])
    useEffect(() => {
        setLoaded(false)
    }, [filteredTags, readOnly])
    useEffect(() => {
        if (wsConnectionStatus === 'Open' && previousConnectionStatus.current !== 'Open') setLoaded(false)
        previousConnectionStatus.current = wsConnectionStatus
    }, [wsConnectionStatus])
    useEffect(() => {
        const newLayers = layoutLayers[layoutId] || []
        let changed = newLayers.length !== currentLayers.length
        setCurrentLayers(newLayers)
        if (!changed) {
            for (let i = 0; i < newLayers.length; i++) {
                if (newLayers[i] !== currentLayers[i]) {
                    setLoaded(false)
                    break
                }
            }
        } else {
            setLoaded(false)
        }
    }, [layoutLayers])

    useEffect(() => {
        if (restoredLayersRef.current) return
        const selectedLayerIds = selectedLayerIdsMap[canvas.id]
        if (selectedLayerIds === undefined) return
        restoredLayersRef.current = true
        const current = layoutLayers[layoutId] ?? []
        const same = current.length === selectedLayerIds.length
            && current.every((id, index) => id === selectedLayerIds[index])
        if (!same) {
            dispatch(setLayoutLayers({
                id: layoutId,
                layers: selectedLayerIds,
            }))
        }
    }, [canvas.id, layoutId, selectedLayerIdsMap, layoutLayers, dispatch])

    const handleModeChange = useCallback((newMode: CanvasMode) => {
        if (!readOnly || newMode === "select") setMode(newMode)
    }, [readOnly])
    useEffect(() => {
        if (readOnly) setMode("select")
    }, [readOnly])
    const changeCanvasSelection = (newSelectionIds: Array<string>) => {
        dispatch(changeSelection({
            canvasId: canvas.id,
            changes: newSelectionIds
        }))
    }
    const startWidgetEdit = (widgetId: string) => {
        dispatch(changeEditWidget({
            canvasId: canvas.id,
            changes: widgetId,
        }))
    }

    return (
        <div className='dvLayoutContent' style={styles}>
            <CanvasToolbar mode={mode} readOnly={readOnly}
                           onModeChange={handleModeChange}
                           showResetScroll={scrollPosition.left !== 0 || scrollPosition.top !== 0}
                           onResetScroll={resetScroll}
            />
            <CanvasContent mode={mode}
                           zoom={zoom}
                           canvasId={canvas.id}
                           layerIds={stripNoLayers(currentLayers)}
                           viewportRef={canvasRef}
                           scrollPosition={scrollPosition}
                           isPanning={isPanning}
                           onChangeSelection={changeCanvasSelection}
                           onStartEdit={startWidgetEdit}
                           onChangeTool={handleModeChange}
                           onNavigate={onNavigate}
                           onNavigateToDocument={onNavigateToDocument}
                           onNavigateToTask={onNavigateToTask}
            />
            {minimapVisible && <CanvasMinimap key={canvas.id}
                widgets={canvasContents[canvas.id]?.widgets ?? []}
                viewportRef={canvasRef}
                scrollPosition={scrollPosition}
                zoom={zoom}
                onNavigate={navigateTo}
            />}
        </div>
    )
}
